import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

/*
 * AcceptInvitationPage — public route at /invitations/:token
 *
 * Renders one of three states:
 *   1. Pending — user is logged in with the right email → "Accept" CTA.
 *   2. Pending — user not logged in, or wrong email → prompts them to sign in
 *      with the invited email (a banner explains they used the wrong one).
 *   3. Already accepted / revoked / expired → friendly explanation + CTA back
 *      to home.
 *
 * After successful acceptance, redirects to /organization/members?org_id=...
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvitationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  const currentUserEmail = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      return (JSON.parse(raw).email || '').toLowerCase();
    } catch { return null; }
  })();
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/invitations/${token}`);
        setInvite(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Invitation not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleAccept = useCallback(async () => {
    setAccepting(true);
    try {
      const authToken = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/invitations/${token}/accept`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      navigate(`/organization/members?org_id=${res.data.org_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not accept the invitation.');
      setAccepting(false);
    }
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <p className="text-gray-600">Loading invitation…</p>
      </div>
    );
  }

  const wrapper = (children) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full" data-testid="invitation-page">
        {children}
      </div>
    </div>
  );

  if (error || !invite) {
    return wrapper(
      <>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation unavailable</h1>
        <p className="text-gray-600 mb-5">{error || 'This invitation link is no longer valid.'}</p>
        <button onClick={() => navigate('/')} className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-medium">
          Go home
        </button>
      </>
    );
  }

  if (invite.status === 'accepted') {
    return wrapper(
      <>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Already accepted</h1>
        <p className="text-gray-600 mb-5">This invitation has already been used.</p>
        <button onClick={() => navigate('/')} className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-medium">
          Go to Tasks
        </button>
      </>
    );
  }

  if (invite.status === 'revoked') {
    return wrapper(
      <>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation revoked</h1>
        <p className="text-gray-600 mb-5">The team owner has cancelled this invitation. Ask them to send a new one.</p>
        <button onClick={() => navigate('/')} className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-medium">
          Go home
        </button>
      </>
    );
  }

  if (invite.status === 'expired') {
    return wrapper(
      <>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation expired</h1>
        <p className="text-gray-600 mb-5">This invitation is more than 7 days old. Ask the team owner to send a new one.</p>
        <button onClick={() => navigate('/')} className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-medium">
          Go home
        </button>
      </>
    );
  }

  // status === 'pending'
  const emailMatches = isLoggedIn && currentUserEmail === (invite.email || '').toLowerCase();

  return wrapper(
    <>
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-3xl mb-4">
        🏢
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-1">
        Join {invite.org_name || 'this team'}
      </h1>
      <p className="text-gray-600 mb-4">
        <strong>{invite.inviter_name || 'A teammate'}</strong> invited you to
        join <strong>{invite.org_name}</strong> as a {invite.role}.
      </p>
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 mb-5">
        Invitation sent to <strong>{invite.email}</strong>
      </div>

      {!isLoggedIn && (
        <>
          <p className="text-sm text-gray-600 mb-3">
            Sign in (or create an account) with <strong>{invite.email}</strong> to accept.
          </p>
          <button
            onClick={() => navigate(`/?next=/invitations/${token}`)}
            data-testid="invitation-signin-cta"
            className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-medium"
          >
            Sign in to continue
          </button>
        </>
      )}

      {isLoggedIn && !emailMatches && (
        <>
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm mb-4">
            You're signed in as <strong>{currentUserEmail}</strong>, but this
            invitation was sent to <strong>{invite.email}</strong>. Sign out and
            sign back in with the right account to accept.
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              navigate(`/?next=/invitations/${token}`);
            }}
            data-testid="invitation-switch-account"
            className="w-full bg-indigo-600 text-white py-2.5 rounded-xl font-medium"
          >
            Sign out and switch account
          </button>
        </>
      )}

      {isLoggedIn && emailMatches && (
        <button
          onClick={handleAccept}
          disabled={accepting}
          data-testid="invitation-accept-cta"
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 disabled:opacity-60 text-white py-2.5 rounded-xl font-medium"
        >
          {accepting ? 'Joining…' : `Accept and join ${invite.org_name}`}
        </button>
      )}
    </>
  );
};

export default AcceptInvitationPage;
